import { useCallback, useMemo, useRef, useState, KeyboardEvent } from "react"
import { GetApp } from "../App"
import { GetShip } from "../App/Context"
import { useRefCallbacks } from "../App/useRefCallbacks"
import { Icon, Icons } from "../Components/Icon"
import { Loading } from "../Components/Loading"
import { Menu, MenuCallbacks, MenuItem, MenuItemDivider } from "../Components/Menu"
import { ApiClient, AccountApi, IAccountSearchResponse } from "../Types"
import { logger } from "../App/Logger"

type SearchToken = [string, string | number];

interface SearchViewProps {
}

const knownTokens = ["ship", "type"];
const validTypes = ["cruise", "day", "log", "note", "trip"];

// Search rows are companions to ranked results/cursors for one submitted query.
// They are deliberately not canonical collection authority for DataStore.
export const SearchDataOwnership = "transient-query-projection" as const;

export const SearchViewData = {
  search(api: ApiClient, query: string): Promise<IAccountSearchResponse> {
    return new AccountApi(api).account.search({ params: {}, body: { query } }).call();
  },

  // Normalize a `key:value` split into a `[key, value]` token, clamping to two parts.
  ensureSearchToken(input: string[]): SearchToken {
    let token = input;
    if (input.length > 2) {
      token = input.slice(0, 2);
    }
    return [token[0] || "", token[1] || ""];
  },

  // A `key:value` fragment is a token only when it has a colon and a known key.
  isValidToken(input: string): boolean {
    return input.indexOf(":") > 0 && knownTokens.indexOf(input.split(":", 2)[0].toLowerCase()) >= 0;
  },

  // Split a raw input string into its recognized tokens and the residual free-text
  // query. When no tokens are present the whole string is the query.
  parseInput(value: string): { tokens: SearchToken[]; query: string } {
    const tokens = value
      .split(" ")
      .filter((q) => SearchViewData.isValidToken(q))
      .map((q) => SearchViewData.ensureSearchToken(q.split(":", 2)));
    const query = tokens.length > 0
      ? value.split(" ").filter((q) => !SearchViewData.isValidToken(q)).join(" ")
      : value;
    return { tokens, query };
  },

  // Human-readable value for a token; `type` values map through the localized label.
  tokenValue(token: SearchToken, t: (key: string) => string): string {
    if (token[0] == "type") {
      if (typeof token[1] == "string" && validTypes.indexOf(token[1]) >= 0) {
        return t(`type_${token[1]}`);
      }
    }
    return token[1].toString();
  },
};

export function SearchView(_: SearchViewProps) {
  const app = GetApp();
  const _ship = GetShip();
  const t = app.getT("SearchView");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRefCallbacks<MenuCallbacks>();
  const [tokens, setTokens] = useState<SearchToken[]>([
    // ["ship", ship.id], ["type", "log"]
  ]);
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<IAccountSearchResponse | undefined>(undefined);
  const [searching, setSearching] = useState<boolean>();

  const performSearch = useCallback(async (query: string, tokens: SearchToken[]) => {
    logger.debug("performSearch", query, tokens);
    if (query == "") {
      return;
    }
    const search = [...(tokens.map((t) => `${t[0]}:${t[1]}`)), query].join(" ");
    if (search == "") {
      return;
    }
    setResults(undefined);
    setSearching(true);
    const result = await SearchViewData.search(app.apiClient, query);
    setResults(result);
    setSearching(false);
  }, [tokens, query]);

  const deleteToken = useCallback((index: number) => {
    setTokens(tokens.toSpliced(index, 1));
    performSearch(query, tokens);
  }, [tokens]);

  const resetSearch = useCallback(() => {
    if (query == "" && tokens.length < 1) {
      menuRef.current?.setSelected(false);
      return;
    }
    setTokens([]);
    setQuery("");
    setResults(undefined);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [tokens, query]);

  const searchKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key == "Enter") {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      updateSearch(true);
    } else if (e.key == " ") {
      updateSearch(false);
    }
  }, [tokens, query]);

  const typeFilter = useMemo(() => {
    const token = tokens.filter((t) => t[0] == "type");
    if (token.length > 0) {
      return token[0][1];
    }
    return undefined;
  }, [tokens]);
  const setTypeFilter = useCallback((type: string | number) => {
    if (typeof type != "string") {
      return;
    }
    if (!typeFilter) {
      setTokens([...tokens, ["type", type]]);
    }
  }, [tokens]);

  const updateSearch = useCallback((perform: boolean = true) => {
    let newQuery = query;
    if (inputRef.current) {
      const value = inputRef.current.value;
      const { tokens: newTokens, query: parsedQuery } = SearchViewData.parseInput(value);
      if (newTokens.length > 0) {
        newQuery = parsedQuery;
        inputRef.current.value = newQuery;
        setTokens([...tokens, ...newTokens]);
      } else {
        newQuery = value;
      }
      setQuery(newQuery);
    }
    if (perform) {
      performSearch(newQuery, tokens);
    }
  }, [query, tokens]);

  function tokenLabel(token: string) {
    if (knownTokens.indexOf(token.toLowerCase())) {
      return t(`token_${token}`);
    }
    return token;
  }

  function tokenValue(token: SearchToken): string {
    return SearchViewData.tokenValue(token, t);
  }

  return (
    <div className="SearchView">
      <Menu button={<Icon icon={Icons.Search} />} buttonClass="button" callbacks={menuRef} global={true}>
        <MenuItem id="search-query" className="SearchView-query-menu-item">
          <div className="SearchView-query">
            {tokens.map((t, i) => {
              return (
                <span key={`search-token-${t[0]}-${t[1]}`} className="SearchView-token" onClick={() => deleteToken(i)}>
                  {tokenLabel(t[0])}: {tokenValue(t)} <Icon icon={Icons.Close} />
                </span>
              );
            })}
            <input name="search" onBlur={() => updateSearch(true)} onKeyDown={searchKeyDown} ref={inputRef} />
            <button className="SearchView-clear" onClick={resetSearch}><Icon icon={Icons.Close} /></button>
          </div>
        </MenuItem>
        {(searching || results) && <MenuItemDivider key="divider" />}
        {searching && (
          <div className="Menu-item disabled">
            <Loading />
          </div>
        )}
        {results && (
          <>
            {(typeFilter ? [typeFilter] : (Object.keys(results.results).toSorted())).flatMap((k, i) => {
              return [
                ...[
                  <div key={`search-result-header-${k}`} className={"Menu-item" + (results.results[k].results.length < 1 ? " disabled" : "")} onClick={() => setTypeFilter(k)}>
                    {t(`type_${k}`)} results ({results.results[k].results.length})
                  </div>,
                  ...(
                    results.results[k].results.map((r) => {
                      return (
                        <div key={`search-result-${k}-${r.id}`} className="Menu-item">
                          {k} {r.id} {Object.entries(r.matches).map((m) => `${m[0]}: ${m[1]}`).join(" ")}
                        </div>
                      );
                    })
                  ),
                ],
                ...((!typeFilter && i < validTypes.length - 1) ? [<MenuItemDivider key="divider" />] : []),
              ];
            })}
          </>
        )}
      </Menu>
    </div>
  );
}
